import React, {
  useState, useEffect, useRef, useCallback, useMemo,
  forwardRef, useImperativeHandle
} from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import useEvent from '../../hooks/useEvent';
import styles from './WordleBoard.module.css';
import {
  decomposeToJamo, composeFromJamo, evaluateGuess, mergeKeyState, CELL_STATE
} from './hangul';
import {
  getRandomWord, lookupByJamo, loadDictionary, isDictionaryLoaded,
  MIN_LENGTH, MAX_LENGTH
} from './words';
import { encodeSeed, decodeSeed } from './seed';
import successSound from '../../assets/sounds/success.mp3';
import failSound from '../../assets/sounds/fail.mp3';

// 모듈 스코프에서 한 번만 생성하여 매 렌더 누수 방지
const successAudio = new Audio(successSound);
const failAudio = new Audio(failSound);
successAudio.volume = 0.5;
failAudio.volume = 0.5;

const MAX_ATTEMPTS = 6;
const DEFAULT_LENGTH = 5;
const BACKSPACE = '⌫';
const ENTER = '↵';

// 예시 이미지와 동일한 3행 자판 배치
const KEYBOARD_ROWS = [
  ['ㅂ', 'ㅈ', 'ㄷ', 'ㄱ', 'ㅅ', 'ㅛ', 'ㅕ', 'ㅑ', 'ㅐ', 'ㅔ'],
  ['ㅁ', 'ㄴ', 'ㅇ', 'ㄹ', 'ㅎ', 'ㅗ', 'ㅓ', 'ㅏ', 'ㅣ', BACKSPACE],
  ['ㅋ', 'ㅌ', 'ㅊ', 'ㅍ', 'ㅠ', 'ㅜ', 'ㅡ', 'ㅒ', 'ㅖ', ENTER]
];

// 두벌식 물리 키보드 매핑 (PC 사용자용)
const QWERTY_TO_JAMO = {
  q: 'ㅂ', w: 'ㅈ', e: 'ㄷ', r: 'ㄱ', t: 'ㅅ', y: 'ㅛ', u: 'ㅕ', i: 'ㅑ', o: 'ㅐ', p: 'ㅔ',
  a: 'ㅁ', s: 'ㄴ', d: 'ㅇ', f: 'ㄹ', g: 'ㅎ', h: 'ㅗ', j: 'ㅓ', k: 'ㅏ', l: 'ㅣ',
  z: 'ㅋ', x: 'ㅌ', c: 'ㅊ', v: 'ㅍ', b: 'ㅠ', n: 'ㅜ', m: 'ㅡ',
  O: 'ㅒ', P: 'ㅖ'
};

const EMOJI = {
  [CELL_STATE.CORRECT]: '🟩',
  [CELL_STATE.PRESENT]: '🟨',
  [CELL_STATE.ABSENT]: '⬜'
};

const WordleBoard = forwardRef(({ seed: seedFromUrl, onClearSeed }, ref) => {
  const { t } = useLanguage();

  const [length, setLength] = useState(DEFAULT_LENGTH);
  const [answer, setAnswer] = useState('');
  const [answerJamo, setAnswerJamo] = useState([]);
  const [seed, setSeed] = useState('');
  const [isCustom, setIsCustom] = useState(false);

  const [guesses, setGuesses] = useState([]); // [{ jamo: [], states: [] }]
  const [current, setCurrent] = useState([]); // 입력 중인 자모
  const [status, setStatus] = useState('playing'); // playing | won | lost
  const [message, setMessage] = useState('');
  const [isShaking, setIsShaking] = useState(false);
  const [revealRow, setRevealRow] = useState(-1);
  const [isSoundMuted, setIsSoundMuted] = useState(false);

  const [showResult, setShowResult] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [createInput, setCreateInput] = useState('');
  const [createdSeed, setCreatedSeed] = useState('');
  const [copyNotice, setCopyNotice] = useState('');

  const messageTimerRef = useRef(null);
  const shakeTimerRef = useRef(null);
  const resultTimerRef = useRef(null);
  const isSoundMutedRef = useRef(false);
  // "아직 아무 시드도 적용하지 않음" 을 뜻한다.
  // null 은 "시드 없는 게임"이라는 유효한 상태와 겹치므로 쓰면 안 된다.
  const appliedSeedRef = useRef(undefined);

  useEffect(() => {
    isSoundMutedRef.current = isSoundMuted;
  }, [isSoundMuted]);

  // 허용 사전은 용량이 커서 별도 청크로 분리해 두었다.
  // 게임에 들어온 시점에 미리 받아두면 첫 제출 때 기다릴 일이 거의 없다.
  useEffect(() => {
    loadDictionary();
  }, []);

  const clearTimers = useCallback(() => {
    [messageTimerRef, shakeTimerRef, resultTimerRef].forEach((timer) => {
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    });
  }, []);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const playSound = useCallback((audio) => {
    if (isSoundMutedRef.current) return;
    try {
      audio.currentTime = 0;
      const p = audio.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch (_) {}
  }, []);

  const showMessage = useCallback((text) => {
    setMessage(text);
    if (messageTimerRef.current) clearTimeout(messageTimerRef.current);
    messageTimerRef.current = setTimeout(() => {
      setMessage('');
      messageTimerRef.current = null;
    }, 2000);
  }, []);

  /** 주어진 단어로 새 게임을 시작한다 */
  const startWithWord = useCallback((word, custom) => {
    const jamo = decomposeToJamo(word);
    if (!jamo) return false;

    clearTimers();
    setAnswer(word);
    setAnswerJamo(jamo);
    setLength(jamo.length);
    setSeed(encodeSeed(word) || '');
    setIsCustom(Boolean(custom));
    setGuesses([]);
    setCurrent([]);
    setStatus('playing');
    setMessage('');
    setRevealRow(-1);
    setShowResult(false);
    return true;
  }, [clearTimers]);

  /** 해당 길이에서 무작위 단어로 새 게임 */
  const startRandom = useCallback((wordLength) => {
    const word = getRandomWord(wordLength);
    if (!word) {
      // 정답 풀이 비는 일은 없어야 하지만, 혹시라도 그러면
      // 빈 게임판을 보여주느니 기본 길이로 한 번 더 시도한다.
      const fallback = getRandomWord(DEFAULT_LENGTH);
      if (fallback) startWithWord(fallback, false);
      return;
    }
    startWithWord(word, false);
  }, [startWithWord]);

  // 최초 진입 및 시드 변경 시
  useEffect(() => {
    if (appliedSeedRef.current === (seedFromUrl || null)) return;
    appliedSeedRef.current = seedFromUrl || null;

    if (seedFromUrl) {
      const decoded = decodeSeed(seedFromUrl);
      const jamo = decoded ? decomposeToJamo(decoded) : null;

      if (jamo && jamo.length >= MIN_LENGTH && jamo.length <= MAX_LENGTH) {
        startWithWord(decoded, true);
        return;
      }
      // 잘못된 시드 → 무작위 게임으로 폴백
      startRandom(DEFAULT_LENGTH);
      showMessage(t('games.wordle.invalidSeed'));
      return;
    }
    startRandom(DEFAULT_LENGTH);
  }, [seedFromUrl, startWithWord, startRandom, showMessage, t]);

  useImperativeHandle(ref, () => ({
    resetGame: () => {
      if (isCustom && answer) {
        // 공유받은 문제는 정답을 유지한 채 다시 푼다
        startWithWord(answer, true);
      } else {
        startRandom(length);
      }
    }
  }), [isCustom, answer, length, startWithWord, startRandom]);

  /**
   * 공유받은 문제를 벗어나 새 무작위 문제로 넘어간다.
   * URL 의 ?seed= 도 지워야 새로고침했을 때 같은 문제로 돌아가지 않는다.
   */
  const startFreshPuzzle = useCallback(() => {
    if (onClearSeed) onClearSeed();
    appliedSeedRef.current = null;
    setIsCustom(false);
    startRandom(length);
  }, [onClearSeed, length, startRandom]);

  const handleLengthChange = (e) => {
    const next = Number(e.target.value);
    setLength(next);
    startRandom(next);
  };

  const triggerShake = useCallback(() => {
    setIsShaking(true);
    if (shakeTimerRef.current) clearTimeout(shakeTimerRef.current);
    shakeTimerRef.current = setTimeout(() => {
      setIsShaking(false);
      shakeTimerRef.current = null;
    }, 450);
  }, []);

  const submitGuess = useCallback(async () => {
    if (status !== 'playing') return;

    if (current.length < length) {
      showMessage(t('games.wordle.tooShort'));
      triggerShake();
      return;
    }

    // 정답과 똑같이 입력했다면 사전 검사를 건너뛴다.
    // 사람 이름처럼 사전에 없는 단어로 낸 문제도 풀 수 있어야 하기 때문이다.
    // (정답과 일치할 때만이므로 아무 단어나 통과하는 것은 아니다)
    const isAnswerItself = current.join('') === answerJamo.join('');

    if (!isAnswerItself) {
      // 사전이 아직 안 왔으면 기다린다.
      // 안 그러면 멀쩡한 단어를 "사전에 없다"고 잘못 거절하게 된다.
      if (!isDictionaryLoaded()) {
        showMessage(t('games.wordle.loadingDict'));
        await loadDictionary();
        if (status !== 'playing') return;
      }

      // 사전에 있는 단어인지 확인
      if (!lookupByJamo(current)) {
        showMessage(t('games.wordle.notInDict'));
        triggerShake();
        return;
      }
    }

    const states = evaluateGuess(current, answerJamo);
    const nextGuesses = [...guesses, { jamo: current, states }];

    setGuesses(nextGuesses);
    setCurrent([]);
    setRevealRow(guesses.length);

    const isWin = states.every((s) => s === CELL_STATE.CORRECT);
    const isLost = !isWin && nextGuesses.length >= MAX_ATTEMPTS;

    if (isWin || isLost) {
      setStatus(isWin ? 'won' : 'lost');
      // 마지막 줄 flip 애니메이션이 끝난 뒤 결과 모달
      const delay = length * 100 + 500;
      if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
      resultTimerRef.current = setTimeout(() => {
        playSound(isWin ? successAudio : failAudio);
        setShowResult(true);
        resultTimerRef.current = null;
      }, delay);
    }
  }, [
    status, current, length, answerJamo, guesses,
    showMessage, triggerShake, playSound, t
  ]);

  const handleKeyInput = useCallback((key) => {
    if (status !== 'playing') return;

    if (key === BACKSPACE) {
      setCurrent((prev) => prev.slice(0, -1));
      return;
    }
    if (key === ENTER) {
      submitGuess();
      return;
    }
    setCurrent((prev) => (prev.length >= length ? prev : [...prev, key]));
  }, [status, length, submitGuess]);

  // 물리 키보드 지원
  const handlePhysicalKey = useCallback((e) => {
    if (showCreate || showResult) return;
    if (e.ctrlKey || e.metaKey || e.altKey) return;

    if (e.key === 'Backspace') {
      e.preventDefault();
      handleKeyInput(BACKSPACE);
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      handleKeyInput(ENTER);
      return;
    }

    // 한글 자판으로 입력한 경우 e.key 가 이미 자모다
    if (/^[ㄱ-ㅣ]$/.test(e.key)) {
      const jamo = decomposeToJamo(e.key) || [e.key];
      jamo.forEach((j) => handleKeyInput(j));
      return;
    }

    const mapped = QWERTY_TO_JAMO[e.key];
    if (mapped) {
      e.preventDefault();
      handleKeyInput(mapped);
    }
  }, [handleKeyInput, showCreate, showResult]);

  useEvent('keydown', handlePhysicalKey);

  // 키보드 색상 누적
  const keyStates = useMemo(() => {
    const map = {};
    for (const { jamo, states } of guesses) {
      jamo.forEach((j, i) => {
        map[j] = mergeKeyState(map[j], states[i]);
      });
    }
    return map;
  }, [guesses]);

  const preview = useMemo(() => composeFromJamo(current), [current]);

  const shareText = useMemo(() => {
    const header = `${t('games.wordle.title')} ${seed} ${
      status === 'won' ? `${guesses.length}/${MAX_ATTEMPTS}` : `X/${MAX_ATTEMPTS}`
    }`;
    const grid = guesses
      .map(({ states }) => states.map((s) => EMOJI[s]).join(''))
      .join('\n');
    return `${header}\n${grid}\n${window.location.origin}${process.env.PUBLIC_URL}/wordle?seed=${seed}`;
  }, [t, seed, status, guesses]);

  const copyToClipboard = useCallback(async (text, notice) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyNotice(notice);
      setTimeout(() => setCopyNotice(''), 2000);
    } catch (err) {
      alert(t('games.wordle.copyFailed'));
    }
  }, [t]);

  // 문제 내기 모달
  const createJamo = useMemo(
    () => decomposeToJamo(createInput.trim()) || [],
    [createInput]
  );
  const createLength = createJamo.length;
  const isCreateValid =
    createLength >= MIN_LENGTH && createLength <= MAX_LENGTH;

  useEffect(() => {
    if (!showCreate) return;
    setCreatedSeed(isCreateValid ? encodeSeed(createInput.trim()) || '' : '');
  }, [showCreate, createInput, isCreateValid]);

  const createUrl = createdSeed
    ? `${window.location.origin}${process.env.PUBLIC_URL}/wordle?seed=${createdSeed}`
    : '';

  const openCreate = () => {
    setCreateInput('');
    setCreatedSeed('');
    setCopyNotice('');
    setShowCreate(true);
  };

  // 처음엔 1줄만 보여주고 시도할 때마다 한 줄씩 늘어난다 (최대 6줄).
  // 게임이 끝나면 그때까지 시도한 줄만 남긴다.
  const visibleRows =
    status === 'playing'
      ? Math.min(guesses.length + 1, MAX_ATTEMPTS)
      : Math.max(guesses.length, 1);

  const rows = Array.from({ length: visibleRows }, (_, rowIndex) => {
    const submitted = guesses[rowIndex];
    const isCurrentRow = rowIndex === guesses.length && status === 'playing';

    return Array.from({ length }, (_, colIndex) => {
      if (submitted) {
        return {
          jamo: submitted.jamo[colIndex],
          state: submitted.states[colIndex],
          revealed: rowIndex === revealRow
        };
      }
      if (isCurrentRow && current[colIndex]) {
        return { jamo: current[colIndex], state: CELL_STATE.FILLED };
      }
      return { jamo: '', state: CELL_STATE.EMPTY };
    });
  });

  return (
    <div className={styles.board}>
      <div className={styles.controlBar}>
        <div className={styles.lengthSelector}>
          <span className={styles.lengthLabel}>{t('games.wordle.length')}</span>
          <select
            value={length}
            onChange={handleLengthChange}
            className={styles.lengthDropdown}
            disabled={isCustom}
          >
            <option value={4}>{t('games.wordle.lengths.4')}</option>
            <option value={5}>{t('games.wordle.lengths.5')}</option>
            <option value={6}>{t('games.wordle.lengths.6')}</option>
          </select>
        </div>
        <div className={styles.controlActions}>
          <button className={styles.createButton} onClick={openCreate}>
            {t('games.wordle.createPuzzle')}
          </button>
          <button
            className={styles.soundToggle}
            onClick={() => setIsSoundMuted((m) => !m)}
            aria-label="sound"
          >
            {isSoundMuted ? '🔇' : '🔊'}
          </button>
        </div>
      </div>

      {isCustom && (
        <div className={styles.seedBadge}>
          {t('games.wordle.sharedPuzzle')} <strong>{seed}</strong>
        </div>
      )}

      <div className={styles.attemptCounter}>
        {Math.min(guesses.length + (status === 'playing' ? 1 : 0), MAX_ATTEMPTS)}
        /{MAX_ATTEMPTS}
      </div>

      <div className={styles.gridWrapper}>
        <div
          className={styles.grid}
          style={{ gridTemplateColumns: `repeat(${length}, 1fr)` }}
        >
          {rows.map((row, rowIndex) =>
            row.map((cell, colIndex) => (
              <div
                key={`${rowIndex}-${colIndex}`}
                className={[
                  styles.cell,
                  styles[cell.state],
                  cell.revealed ? styles.reveal : '',
                  isShaking && rowIndex === guesses.length ? styles.shake : ''
                ].filter(Boolean).join(' ')}
                style={cell.revealed ? { animationDelay: `${colIndex * 100}ms` } : undefined}
              >
                {cell.jamo}
              </div>
            ))
          )}
        </div>
      </div>

      {/* 안내 문구는 자판 바로 위에 띄운다.
          게임판 위쪽에 두면 휴대폰에서 시선 밖이라 못 보고 지나친다. */}
      <div className={styles.previewBar}>
        {message ? (
          <span className={styles.toastText}>{message}</span>
        ) : (
          preview || (
            <span className={styles.previewPlaceholder}>
              {t('games.wordle.inputHint')}
            </span>
          )
        )}
      </div>

      <div className={styles.keyboard}>
        {KEYBOARD_ROWS.map((row, rowIndex) => (
          <div key={rowIndex} className={styles.keyboardRow}>
            {row.map((key) => {
              const isAction = key === BACKSPACE || key === ENTER;
              return (
                <button
                  key={key}
                  className={[
                    styles.key,
                    isAction ? styles.actionKey : '',
                    keyStates[key] ? styles[keyStates[key]] : ''
                  ].filter(Boolean).join(' ')}
                  onClick={() => handleKeyInput(key)}
                >
                  {key}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {showResult && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{status === 'won' ? t('games.wordle.win') : t('games.wordle.lose')}</h2>
            <p>
              {t('games.wordle.answerWas')}: <strong>{answer}</strong>
            </p>
            <p>
              {t('games.wordle.attempts')}:{' '}
              <strong>
                {status === 'won' ? guesses.length : 'X'}/{MAX_ATTEMPTS}
              </strong>
            </p>
            <div className={styles.emojiGrid}>
              {guesses.map(({ states }, i) => (
                <div key={i}>{states.map((s) => EMOJI[s]).join('')}</div>
              ))}
            </div>
            {copyNotice && <p className={styles.copyNotice}>{copyNotice}</p>}
            <div className={`modal-buttons ${styles.resultButtons}`}>
              <button
                onClick={() => copyToClipboard(shareText, t('games.wordle.copied'))}
              >
                {t('games.wordle.shareResult')}
              </button>
              {isCustom ? (
                // 공유받은 문제는 "다시 풀기" 만 있으면 같은 문제에 갇힌다.
                // 새 문제로 나갈 길을 함께 열어둔다.
                <>
                  <button onClick={() => startWithWord(answer, true)}>
                    {t('playAgain')}
                  </button>
                  <button onClick={startFreshPuzzle}>
                    {t('games.wordle.newPuzzle')}
                  </button>
                </>
              ) : (
                <button onClick={() => startRandom(length)}>
                  {t('playAgain')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showCreate && (
        <div className="modal-overlay">
          <div className="modal">
            <h2>{t('games.wordle.createPuzzle')}</h2>
            <p style={{ whiteSpace: 'pre-line' }}>
              {t('games.wordle.createHint')}
            </p>
            <input
              className={styles.createInput}
              value={createInput}
              onChange={(e) => setCreateInput(e.target.value)}
              placeholder={t('games.wordle.createPlaceholder')}
              maxLength={6}
            />
            <p className={styles.createStatus}>
              {createInput.trim() === ''
                ? ' '
                : createLength === 0
                  ? t('games.wordle.createNotHangul')
                  : isCreateValid
                    ? t('games.wordle.createLength').replace('{n}', createLength)
                    : t('games.wordle.createOutOfRange')}
            </p>

            {createdSeed && (
              <div className={styles.seedBox}>
                <span className={styles.seedLabel}>{t('games.wordle.seedLabel')}</span>
                <strong className={styles.seedValue}>{createdSeed}</strong>
              </div>
            )}
            {copyNotice && <p className={styles.copyNotice}>{copyNotice}</p>}

            <div className="modal-buttons">
              <button
                disabled={!createdSeed}
                onClick={() => copyToClipboard(createUrl, t('games.wordle.linkCopied'))}
              >
                {t('games.wordle.copyLink')}
              </button>
              <button onClick={() => setShowCreate(false)}>
                {t('games.wordle.closeModal')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default WordleBoard;
